import {
  ChartTooltip,
  ChartTooltipItem,
  ChartTooltipLabel,
  ChartTooltipProps,
} from '@design/charts/chart-tooltip/ChartTooltip';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import {
  checkCellValueStyle,
  NumericalCellValueStyle,
} from '@helpers/utils/table/table';
import clsx from 'clsx';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { HighLowTripleLineChartProps } from '../../HighLowTripleLineChart';
import { HighLowDataPoint } from '../../HighLowTripleLineChart.types';
import styles from './HighLowTripleLineChartTooltip.module.scss';

interface HighLowTripleLineChartTooltipProps
  extends Omit<ChartTooltipProps, 'children'> {
  tooltipLabels: HighLowTripleLineChartProps['tooltipLabels'];
  hoveredTs: HighLowDataPoint;
  hoveredSnakeTs?: number;
  numberFormatter: (value: number) => string;
}

export const HighLowTripleLineChartTooltip = forwardRef<
  HTMLDivElement,
  HighLowTripleLineChartTooltipProps
>(
  (
    {
      hoveredTs,
      hoveredSnakeTs,
      title,
      tooltipLabels,
      numberFormatter,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation('chart');

    const keys: Array<keyof HighLowDataPoint> = ['high', 'median', 'low'];
    return (
      <ChartTooltip
        className={styles.styledChartTooltip}
        ref={ref}
        {...rest}
        title={title}
      >
        <div className={styles.itemsContainer}>
          {keys.map(key => {
            const numericalStyle = checkCellValueStyle(hoveredTs[key]);
            return (
              <ChartTooltipItem key={key}>
                <ChartTooltipLabel>{tooltipLabels[key]}</ChartTooltipLabel>
                <span
                  className={clsx(styles.coloredValue, {
                    [semanticColorStyles.directionPositive]:
                      numericalStyle === NumericalCellValueStyle.Positive,
                    [semanticColorStyles.directionNegative]:
                      numericalStyle === NumericalCellValueStyle.Negative,
                    [semanticColorStyles.directionNeutralDefault]:
                      numericalStyle === NumericalCellValueStyle.Neutral,
                  })}
                >
                  {numberFormatter(hoveredTs[key])}
                </span>
              </ChartTooltipItem>
            );
          })}
        </div>
        {hoveredSnakeTs != null &&
          (() => {
            const numericalStyle = checkCellValueStyle(hoveredSnakeTs);
            return (
              <>
                <div className={styles.separator} />
                <div className={styles.itemsContainer}>
                  <ChartTooltipItem>
                    <ChartTooltipLabel>
                      {t('chart:metrics.cumulativeReturn')}
                    </ChartTooltipLabel>
                    <span
                      className={clsx(styles.coloredValue, {
                        [semanticColorStyles.directionPositive]:
                          numericalStyle === NumericalCellValueStyle.Positive,
                        [semanticColorStyles.directionNegative]:
                          numericalStyle === NumericalCellValueStyle.Negative,
                        [semanticColorStyles.directionNeutralDefault]:
                          numericalStyle === NumericalCellValueStyle.Neutral,
                      })}
                    >
                      {numberFormatter(hoveredSnakeTs)}
                    </span>
                  </ChartTooltipItem>
                </div>
              </>
            );
          })()}
      </ChartTooltip>
    );
  }
);
